import React, { useEffect, useRef, useState } from "react";
import { FaHistory } from "react-icons/fa";
import axiosInstance from "../api/axiosInstance";

const ACTOR_LABELS = { owner: "Owner", member: "Member", staff: "Staff" };
const COMPACT_QUERY = "(max-width: 900px)";

export function apiError(err, fallback = "Something went wrong") {
  return err?.response?.data?.message || err?.response?.data?.error || fallback;
}

export function fmtWhen(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

/** True on tablet / mobile widths, where lists render as cards instead of a table. */
export function useCompactView() {
  const matches = () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(COMPACT_QUERY).matches;
  const [compact, setCompact] = useState(matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(COMPACT_QUERY);
    const onChange = () => setCompact(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return compact;
}

/** Oval "Info" button that shows the page description in a popover. */
export function InfoTip({ text, label = "About this page" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!text) return null;
  return (
    <span className="vault-info" ref={ref}>
      <button
        type="button"
        className="vault-info__btn"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Info
      </button>
      {open ? (
        <div className="vault-info__pop" role="tooltip">
          {text}
        </div>
      ) : null}
    </span>
  );
}

export function ActivityButton({ open, onToggle, className = "" }) {
  return (
    <button
      type="button"
      className={`vault-btn${className ? ` ${className}` : ""}${open ? " is-active" : ""}`}
      aria-expanded={open}
      onClick={onToggle}
    >
      <FaHistory /> {open ? "Hide activity" : "Activity log"}
    </button>
  );
}

/** Outlined row icon (same look as the Leads table actions). tone: view | edit | delete | test. */
export function RowAction({ tone = "view", title, onClick, disabled, active, busy, children }) {
  return (
    <button
      type="button"
      className={`vault-action vault-action--${tone}${active ? " is-active" : ""}${busy ? " is-busy" : ""}`}
      title={title}
      aria-label={title}
      aria-pressed={active === undefined ? undefined : active}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/** Loads `${basePath}/audit` while `enabled`, again whenever `refreshKey` changes. */
export function useVaultAudit(basePath, refreshKey, enabled) {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setError("");
    axiosInstance
      .get(`${basePath}/audit`)
      .then((res) => active && setEntries(res.data?.data?.entries || []))
      .catch((err) => active && setError(apiError(err, "Could not load the activity log.")));
    return () => {
      active = false;
    };
  }, [basePath, refreshKey, enabled]);

  return { entries, error };
}

/** Who opened / changed the vault, denied attempts and staff password resets. */
export function AuditTable({ entries, error, showItem = true, emptyText = "No activity yet." }) {
  if (error) return <div className="vault-alert vault-alert--error">{error}</div>;
  if (entries == null) return <div className="vault-empty">Loading…</div>;
  if (!entries.length) return <div className="vault-empty">{emptyText}</div>;
  return (
    <div className="vault-audit__wrap">
      <table className="vault-audit__table">
        <thead>
          <tr>
            <th>When</th>
            <th>Who</th>
            <th>Action</th>
            {showItem ? <th>Item</th> : null}
            <th>Details</th>
            <th>IP</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.Audit_Id} className={e.Action === "unlock_denied" ? "is-denied" : ""}>
              <td data-label="When">{fmtWhen(e.Created_On)}</td>
              <td data-label="Who">
                {e.Actor_Name || "—"}
                <span className={`vault-badge vault-audit__role-inline${e.Actor_Kind === "staff" ? " vault-badge--staff" : ""}`}>
                  {ACTOR_LABELS[e.Actor_Kind] || "Owner"}
                </span>
              </td>
              <td data-label="Role" className="vault-audit__role">
                <span className={`vault-badge${e.Actor_Kind === "staff" ? " vault-badge--staff" : ""}`}>
                  {ACTOR_LABELS[e.Actor_Kind] || "Owner"}
                </span>
              </td>
              <td data-label="Action">{e.Action_Label}</td>
              {showItem ? <td data-label="Item">{e.Vault_Name || "—"}</td> : null}
              <td data-label="Details">{e.Detail || ""}</td>
              <td data-label="IP">{e.Ip_Address || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}